/**
 * The comms-admin kinds in Boombox's ⌘K palette, and the one gate in front
 * of them. A non-admin gets no kinds and no list is fetched for them, so the
 * palette never names an admin page on their behalf. The templates and
 * senders lists are not admin data on the server (the Templates page and the
 * Send page's sender picker read them for every user), so this gate is the
 * only one these kinds have. Relative imports only, so bun test can load this
 * module.
 */

import { cachedList, type SearchableKind, settingsSearchKind } from '@ordo/agent-embed'

import { senderHits, type SenderSearchRow, templateHits, type TemplateSearchRow } from './admin-search'
import { SETTINGS_NAV } from './settings-nav'

export const TEMPLATES_LIST_URL = '/api/templates?take=100'
export const SENDERS_LIST_URL = '/api/senders'
export const SMS_SENDERS_LIST_URL = '/api/senders?method=SMS'

/** Both list endpoints wrap their rows in `{ data }`; a failed load is []. */
async function loadData<T>(url: string): Promise<T[]> {
  const res = await fetch(url, { credentials: 'same-origin' })
  if (!res.ok) {
    return []
  }
  const body = (await res.json()) as { data?: T[] }
  return body.data ?? []
}

// Small lists (tens of templates; SendGrid caps verified senders at 100),
// fetched once and reused across keystrokes (`cachedList`, 60s). Templates
// are paginated server-side, so ask for the 100-row maximum of active ones.
const loadTemplates = cachedList(() => loadData<TemplateSearchRow>(TEMPLATES_LIST_URL))
const loadSenders = cachedList(async () => {
  const lists = await Promise.all([
    loadData<SenderSearchRow>(SENDERS_LIST_URL),
    loadData<SenderSearchRow>(SMS_SENDERS_LIST_URL),
  ])
  return lists.flat()
})

const ADMIN_SEARCHABLE: SearchableKind[] = [
  settingsSearchKind(SETTINGS_NAV),
  {
    kind: 'templates',
    label: 'TEMPLATES',
    search: async (q) => templateHits(q, await loadTemplates()),
  },
  {
    kind: 'senders',
    label: 'SENDERS',
    search: async (q) => senderHits(q, await loadSenders()),
  },
]

/** The kinds `isAdmin` may search: Settings, Templates and Senders for a comms admin, none for anyone else. */
export function adminSearchKinds(isAdmin: boolean): SearchableKind[] {
  return isAdmin ? ADMIN_SEARCHABLE : []
}

/** Load the admin lists ahead of the first keystroke — for a comms admin only. */
export function warmAdminSearch(isAdmin: boolean): void {
  if (!isAdmin) {
    return
  }
  void loadTemplates()
  void loadSenders()
}
